/**
 * /downloads — the canonical place to get every GoodShowHQ build.
 *
 * Three ways to run the always-on part of GoodShowHQ (the piece that
 * actually watches the kid's feed and applies your decisions):
 *
 *   1. Home Assistant add-on — the best one if you have an HA box:
 *      it runs 24/7 without leaving a laptop open. Listed first for
 *      that reason.
 *   2. Desktop app — macOS / Windows / Linux, same engine.
 *   3. Mobile app — reviewing, not running. Cross-linked to /get.
 *
 * Versions come from `lib/releases.ts`, which is generated by
 * `scripts/set-release-version.cjs` alongside the desktop and add-on
 * manifests — the number here is by construction the number that was
 * built and published.
 */
import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import {goodShowHQTheme} from '../../theme/planapusTheme';
import logoMark from '../../assets/brand/logo-mark.svg';
import BrandChip from '../common/BrandChip';
import {
  AppleIcon,
  HomeAssistantIcon,
  LinuxIcon,
  WindowsIcon,
} from '../common/PlatformIcons';
import {
  ALL_RELEASES_URL,
  DESKTOP_DOWNLOADS,
  HA_ADDON_ADD_REPO_URL,
  HA_ADDON_REPO_URL,
  LINUX_DEB_URL,
  RELEASE_VERSION,
  detectPlatform,
  fetchLatestVersion,
  type PlatformId,
} from '../../lib/releases';

/**
 * The download URLs are permanent (latest/download redirect), but the
 * version LABEL would otherwise be frozen at the compiled-in
 * RELEASE_VERSION until the next site deploy. Ask GitHub once for the
 * live latest version and share it across every chip. Module-level
 * memo so N chips = 1 request; RELEASE_VERSION is the instant first
 * paint and the fallback if the request fails.
 */
let latestVersionPromise: Promise<string> | null = null;
function useReleaseVersion(): string {
  const [version, setVersion] = useState(RELEASE_VERSION);
  useEffect(() => {
    if (!latestVersionPromise) latestVersionPromise = fetchLatestVersion();
    let active = true;
    latestVersionPromise.then(v => {
      if (active) setVersion(v);
    });
    return () => {
      active = false;
    };
  }, []);
  return version;
}

const C = {
  white: '#FFFFFF',
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1E293B',
  slate900: '#0F172A',
  indigo50: '#EEF2FF',
  indigo100: '#E0E7FF',
  indigo600: '#4F46E5',
  indigo700: '#4338CA',
  emerald500: '#10B981',
  amber50: '#FFFBEB',
  amber200: '#FDE68A',
  amber800: '#92400E',
};

function Logo(): React.JSX.Element {
  return (
    <Box
      component="a"
      href="/"
      sx={{display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none'}}
    >
      <Box component="img" src={logoMark} alt="" aria-hidden sx={{height: 28, width: 28}} />
      <Typography
        component="span"
        sx={{fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}
      >
        GoodShowHQ
      </Typography>
    </Box>
  );
}

/** Every download surface carries the version it will actually give you. */
function VersionChip({label}: {label?: string}): React.JSX.Element {
  const version = useReleaseVersion();
  return (
    <Chip
      label={label ?? `v${version}`}
      size="small"
      sx={{
        height: 22,
        borderRadius: '6px',
        bgcolor: C.slate100,
        color: C.slate600,
        fontSize: 11,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
}

const PLATFORM_ICON: Record<PlatformId, React.ElementType> = {
  'mac-apple-silicon': AppleIcon,
  'mac-intel': AppleIcon,
  windows: WindowsIcon,
  linux: LinuxIcon,
};

function DesktopCard({detected}: {detected: PlatformId | null}): React.JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${C.slate200}`,
        bgcolor: C.white,
        p: {xs: 2.5, md: 3.5},
        boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 0.5}}>
        <Typography
          component="h2"
          sx={{fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}
        >
          Desktop app
        </Typography>
        <VersionChip />
      </Stack>
      <Typography sx={{fontSize: 14, lineHeight: 1.6, color: C.slate600, maxWidth: 560}}>
        Sign in to your kid&apos;s YouTube once. GoodShowHQ watches their feed and applies
        the decisions you make in the app — for as long as your computer is on.
      </Typography>

      <Box
        sx={{
          mt: 3,
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
          gap: 1.5,
        }}
      >
        {DESKTOP_DOWNLOADS.map(d => {
          const Icon = PLATFORM_ICON[d.id];
          const isDetected = d.id === detected;
          return (
            <Button
              key={d.id}
              href={d.url}
              variant={isDetected ? 'contained' : 'outlined'}
              startIcon={<Icon size={18} />}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                textTransform: 'none',
                ...(isDetected
                  ? {
                      bgcolor: C.indigo600,
                      color: C.white,
                      '&:hover': {bgcolor: C.indigo700},
                    }
                  : {
                      border: `1px solid ${C.slate200}`,
                      color: C.slate900,
                      '&:hover': {bgcolor: C.slate50, border: `1px solid ${C.slate200}`},
                    }),
              }}
            >
              <Box sx={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
                <Box component="span" sx={{fontSize: 14, fontWeight: 700, lineHeight: 1.2}}>
                  {d.label}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    lineHeight: 1.4,
                    opacity: isDetected ? 0.85 : 0.7,
                  }}
                >
                  {d.detail}
                  {isDetected ? ' · detected' : ''}
                </Box>
              </Box>
            </Button>
          );
        })}
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{mt: 2, flexWrap: 'wrap', rowGap: 0.5, fontSize: 12}}
      >
        <Box
          component="a"
          href={LINUX_DEB_URL}
          sx={{color: C.slate500, textDecoration: 'none', '&:hover': {color: C.slate900}}}
        >
          Linux .deb (Debian / Ubuntu / Pi OS)
        </Box>
        <Box
          component="a"
          href={ALL_RELEASES_URL}
          target="_blank"
          rel="noopener"
          sx={{color: C.slate500, textDecoration: 'none', '&:hover': {color: C.slate900}}}
        >
          All releases & release notes
        </Box>
      </Stack>

      {/*
        The app isn't code-signed yet, so macOS quarantines it and
        Windows SmartScreen warns. Saying so plainly beats a parent
        hitting "app is damaged" and assuming they got malware.
      */}
      <Box
        sx={{
          mt: 2.5,
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          borderRadius: '12px',
          border: `1px solid ${C.amber200}`,
          bgcolor: C.amber50,
          px: 1.75,
          py: 1.25,
        }}
      >
        <InfoOutlined sx={{fontSize: 16, color: C.amber800, mt: '1px'}} />
        <Typography sx={{fontSize: 12.5, lineHeight: 1.5, color: C.amber800}}>
          <Box component="strong" sx={{fontWeight: 700}}>
            First launch:
          </Box>{' '}
          the app isn&apos;t notarized yet. On macOS, right-click the app → <b>Open</b> →{' '}
          <b>Open</b>. On Windows, click <b>More info</b> → <b>Run anyway</b>. You only do
          this once.
        </Typography>
      </Box>
    </Box>
  );
}

function HomeAssistantCard(): React.JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${C.slate200}`,
        bgcolor: C.white,
        p: {xs: 2.5, md: 3.5},
        boxShadow: '0 1px 2px rgba(15,23,42,0.05)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{mb: 0.5}}>
        <HomeAssistantIcon size={22} />
        <Typography
          component="h2"
          sx={{fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}
        >
          Home Assistant add-on
        </Typography>
        <VersionChip />
        <Chip
          label="Recommended"
          size="small"
          sx={{
            height: 22,
            borderRadius: '6px',
            bgcolor: C.indigo50,
            color: C.indigo700,
            fontSize: 11,
            fontWeight: 700,
          }}
        />
      </Stack>
      <Typography sx={{fontSize: 14, lineHeight: 1.6, color: C.slate600, maxWidth: 560}}>
        The same engine as the desktop app, running on your Home Assistant box — so it
        keeps working when your laptop is closed. Adds sensors for what each kid is
        watching, so you can build automations around them.
      </Typography>

      <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} sx={{mt: 3}}>
        <Button
          href={HA_ADDON_ADD_REPO_URL}
          target="_blank"
          rel="noopener"
          variant="contained"
          startIcon={<HomeAssistantIcon size={18} color="#FFFFFF" />}
          sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 700,
            bgcolor: C.slate900,
            color: C.white,
            '&:hover': {bgcolor: C.slate800},
          }}
        >
          Add to my Home Assistant
        </Button>
        <Button
          href={HA_ADDON_REPO_URL}
          target="_blank"
          rel="noopener"
          variant="outlined"
          sx={{
            px: 2.5,
            py: 1.5,
            borderRadius: '12px',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 700,
            border: `1px solid ${C.slate200}`,
            color: C.slate900,
            '&:hover': {bgcolor: C.slate50, border: `1px solid ${C.slate200}`},
          }}
        >
          Setup instructions
        </Button>
      </Stack>

      <Typography sx={{mt: 2, fontSize: 12, color: C.slate500}}>
        Opens your own Home Assistant and offers to add the GoodShowHQ repository. Then:
        Settings → Add-ons → GoodShowHQ → Install.
      </Typography>
    </Box>
  );
}

function MobileCard(): React.JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${C.slate200}`,
        bgcolor: C.slate50,
        p: {xs: 2.5, md: 3},
      }}
    >
      <Stack
        direction={{xs: 'column', md: 'row'}}
        spacing={2}
        alignItems={{xs: 'flex-start', md: 'center'}}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 40,
              width: 40,
              borderRadius: '12px',
              bgcolor: C.indigo100,
              color: C.indigo600,
            }}
          >
            <PhoneIphoneRounded sx={{fontSize: 20}} />
          </Box>
          <Box>
            <Typography sx={{fontSize: 16, fontWeight: 800, color: C.slate900}}>
              Reviewing on your phone
            </Typography>
            <Typography sx={{fontSize: 13, color: C.slate600}}>
              The iPhone app is where you approve or block — swipe through what GoodShowHQ
              flagged.
            </Typography>
          </Box>
        </Stack>
        <Button
          href="#/get"
          endIcon={<ArrowForwardRounded sx={{fontSize: 16}} />}
          sx={{
            flexShrink: 0,
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 700,
            color: C.indigo600,
            '&:hover': {bgcolor: C.indigo50},
          }}
        >
          Get the mobile app
        </Button>
      </Stack>
    </Box>
  );
}

export default function DownloadsPage(): React.JSX.Element {
  const [detected, setDetected] = useState<PlatformId | null>(null);
  const version = useReleaseVersion();
  useEffect(() => {
    setDetected(detectPlatform());
  }, []);

  return (
    <ThemeProvider theme={goodShowHQTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(180deg, ${C.white} 0%, ${C.slate50} 100%)`,
          color: C.slate900,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <Box
          component="header"
          sx={{
            borderBottom: `1px solid ${C.slate200}`,
            bgcolor: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <Container maxWidth="lg" sx={{px: 3}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
              }}
            >
              <Logo />
              <Box
                component="a"
                href="/"
                sx={{
                  textDecoration: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.slate400,
                  '&:hover': {color: C.slate700},
                }}
              >
                ← Back to GoodShowHQ
              </Box>
            </Box>
          </Container>
        </Box>

        <Container component="main" maxWidth="md" sx={{px: 3, pt: 6, pb: 10}}>
          <Box sx={{textAlign: 'center'}}>
            <BrandChip label={`Latest release · v${version}`} />
            <Typography
              component="h1"
              sx={{
                mt: 2,
                fontSize: {xs: 32, md: 44},
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Download GoodShowHQ
            </Typography>
            <Typography
              sx={{
                mt: 2,
                mx: 'auto',
                maxWidth: 560,
                fontSize: 16,
                lineHeight: 1.6,
                color: C.slate600,
              }}
            >
              GoodShowHQ needs one always-on connection to your kid&apos;s YouTube. Run it
              on your Home Assistant box, or on any computer you leave on.
            </Typography>
          </Box>

          <Stack spacing={2.5} sx={{mt: 5}}>
            <HomeAssistantCard />
            <DesktopCard detected={detected} />
            <MobileCard />
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{mt: 5, flexWrap: 'wrap', rowGap: 1, fontSize: 12, color: C.slate500}}
          >
            {['Free to use', 'Your YouTube session stays on your machine', 'COPPA-compliant'].map(
              t => (
                <Box
                  key={t}
                  component="span"
                  sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}
                >
                  <CheckCircleRounded sx={{fontSize: 14, color: C.emerald500}} />
                  {t}
                </Box>
              ),
            )}
          </Stack>
        </Container>

        <Box
          component="footer"
          sx={{borderTop: `1px solid ${C.slate200}`, bgcolor: C.white, py: 4}}
        >
          <Container maxWidth="lg" sx={{px: 3}}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                alignItems: {xs: 'flex-start', md: 'center'},
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Logo />
              <Typography sx={{fontSize: 12, color: C.slate500}}>
                Not affiliated with YouTube, Google LLC, Apple, Microsoft, or the Home
                Assistant project. © 2026 GoodShowHQ, Inc.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
